import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwörter stimmen nicht überein."); return; }
    if (password.length < 8) { setErr("Passwort muss mind. 8 Zeichen lang sein."); return; }
    if (!token) { setErr("Kein Token in der URL."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      await checkAuth();
      toast.success("Passwort erfolgreich geändert.");
      nav("/dashboard");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Rücksetzung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="reset-password-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 max-w-md w-full p-10">
        <h1 className="text-3xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
          Neues Passwort setzen
        </h1>
        <p className="text-slate-600 text-sm mb-6">Bitte wählen Sie ein sicheres Passwort (mind. 8 Zeichen).</p>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="reset-pw" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Neues Passwort</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input id="reset-pw" data-testid="reset-input-password" type="password" required minLength={8} maxLength={128}
                     value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-11" />
            </div>
          </div>
          <div>
            <Label htmlFor="reset-pw-confirm" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Passwort wiederholen</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input id="reset-pw-confirm" data-testid="reset-input-confirm" type="password" required minLength={8} maxLength={128}
                     value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10 h-11" />
            </div>
          </div>
          {err && <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{err}</div>}
          <Button type="submit" disabled={busy} data-testid="reset-submit-btn"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Passwort setzen & anmelden
          </Button>
          <Link to="/login" className="block text-center text-sm text-slate-600 hover:text-slate-900 underline" data-testid="reset-back-login">
            ← Zurück zur Anmeldung
          </Link>
        </form>
      </div>
    </div>
  );
}
